import { Box, Button, Dialog, Flex, Heading, Input, Stack, Text, Badge } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { apiDelete, apiGet } from '../api.js';
import { useAuth } from '../context/AuthContext';
import AddClientModal from '../components/AddClientModal.jsx';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);

  // Delete confirmation states
  const [deleteClient, setDeleteClient] = useState(null);
  const [deleteArticles, setDeleteArticles] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchClients = async () => {
    try {
      const result = await apiGet('/clients');
      setClients(result.clients || []);
    } catch (err) {
      setError(err.message || 'Failed to load clients');
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const filteredClients = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return clients;
    return (clients || []).filter((client) => {
      const name = (client.name || '').toLowerCase();
      const contact = String(client.contact || '').toLowerCase();
      return name.includes(q) || contact.includes(q);
    });
  }, [clients, searchQuery]);

  const handleAddSuccess = (newClient, isEdit) => {
    fetchClients();
    setAddModalOpen(false);
    setEditClient(null);
  };

  const openDeleteConfirm = async (client) => {
    setDeleteClient(client);
    setDeleteArticles([]);
    try {
      const res = await apiGet(`/articles?clientId=${client._id}&status=active`);
      setDeleteArticles(res.articles || []);
    } catch (err) {
      // If fetch fails, still show dialog but with no articles
      setDeleteArticles([]);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteClient) return;
    setDeleteLoading(true);
    try {
      await apiDelete(`/clients/${deleteClient._id}`);
      setClients(prev => prev.filter(c => c._id !== deleteClient._id));
      setDeleteClient(null);
    } catch (err) {
      setError(err.message || 'Failed to delete client');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Stack spacing={{ base: 4, md: 6 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <Heading size={{ base: 'md', md: 'lg' }}>Clients</Heading>
        {user?.permissions?.clients?.create && (
          <Button colorScheme="blue" size="sm" onClick={() => { setEditClient(null); setAddModalOpen(true); }}>
            + Add New Client
          </Button>
        )}
      </Flex>

      {error && <Text color="red.500">{error}</Text>}

      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 3, md: 4 }}>
        <Box mb={3} maxW={{ base: '100%', md: '360px' }}>
          <Input
            placeholder="Search client by name or contact..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Box>
        <Box overflowX="auto">
          <table style={{ width: '100%', minWidth: '420px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Contact</th>
                <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length ? filteredClients.map(client => (
                <tr key={client._id}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{client.name}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{client.contact || 'N/A'}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                    {user?.permissions?.clients?.edit && (
                      <Button size="xs" colorScheme="blue" variant="outline" mr={2} onClick={() => { setEditClient(client); setAddModalOpen(true); }}>
                        Edit
                      </Button>
                    )}
                    {user?.permissions?.clients?.delete && (
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => openDeleteConfirm(client)}>
                        Delete
                      </Button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td style={{ padding: '8px' }} colSpan={3}>
                    {searchQuery.trim() ? 'No clients match your search.' : 'No clients found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>

      {/* Add/Edit Client Modal */}
      <AddClientModal
        isOpen={addModalOpen}
        onClose={() => { setAddModalOpen(false); setEditClient(null); }}
        onSuccess={handleAddSuccess}
        editClient={editClient}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog.Root
        open={!!deleteClient}
        onOpenChange={(e) => { if (!e.open) setDeleteClient(null); }}
        size="md"
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Delete Client: {deleteClient?.name}</Dialog.Title>
              <Dialog.CloseTrigger onClick={() => setDeleteClient(null)} />
            </Dialog.Header>
            <Dialog.Body>
              <Stack spacing={3}>
                <Text color="red.600" fontWeight="bold">
                  This will permanently delete the client and all their articles/ledgers.
                </Text>
                {deleteArticles.length > 0 ? (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Active Transactions ({deleteArticles.length}):</Text>
                    <Box maxH="200px" overflowY="auto" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2}>
                      {deleteArticles.map(article => (
                        <Flex key={article._id} justify="space-between" py={1} borderBottom="1px solid" borderColor="gray.100">
                          <Text fontSize="sm">{article.nameOrNo || 'N/A'}</Text>
                          <Flex gap={2}>
                            <Badge colorScheme="blue">₹{article.totalPrincipal || article.amount || 0}</Badge>
                            <Text fontSize="xs" color="gray.500">{formatDate(article.date)}</Text>
                          </Flex>
                        </Flex>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.500">No active transactions for this client.</Text>
                )}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={() => setDeleteClient(null)}>Cancel</Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} isLoading={deleteLoading}>Delete</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Stack>
  );
};

export default Clients;
